import { AnimaAPI } from './api';
import { AnimaAuth, type LoginParams } from './auth';
import { createAnimaDB } from './db';
import { AnimaSettings } from './settings';
import type { User, AppSettings } from './types';
import { AnimaUser } from './user';
import { isCDNBuild } from './utils/build';
import { enforceAuthIfRequired } from './utils/enforceAuth';

export type { AuthTheme } from './auth';
export type { User, AppSettings } from './types';
export * from './errors';
export { AnimaSDK } from './sdk';

declare global {
  interface Window {
    anima: {
      db: Record<string, any>;
      auth: {
        login: (params?: LoginParams) => Promise<void>;
        logout: () => Promise<void>;
      };
      user: {
        me: () => Promise<User | null>;
      };
      settings: {
        getSettings: () => Promise<AppSettings>;
      };
      version: string;
    };

    __ANIMA_PLAYGROUND_ID__: string;
    __ANIMA_TOKEN__: string | undefined;
    __ANIMA_PLAYGROUND_TOKEN__: string | undefined;

    // May be defined as overrides
    __ANIMA_PLAYGROUND_API_BASE_URL__: string | undefined;
    __ANIMA_ACCOUNTS_BASE_URL__: string | undefined;
  }

  // Build-time constants replaced by esbuild during bundling
  const __DEFAULT_ANIMA_PLAYGROUND_API_BASE_URL__: string;
  const __DEFAULT_ANIMA_ACCOUNTS_BASE_URL__: string;
  const __ANIMA_SDK_VERSION__: string;
  const __BUILD_FORMAT__: 'esm' | 'cjs' | 'iife' | 'unknown';
}

// If global (iife) build (CDN script), expose Anima SDK on window.anima
if (isCDNBuild()) {
  const animaAuth = new AnimaAuth();
  const animaAPI = new AnimaAPI({
    animaAuth,
  });
  const animaDB = createAnimaDB({
    animaAPI,
  });
  const animaUser = new AnimaUser({
    animaAPI,
  });
  const animaSettings = new AnimaSettings();

  window.anima = {
    db: animaDB,
    auth: animaAuth,
    user: animaUser,
    settings: animaSettings,
    version: __ANIMA_SDK_VERSION__,
  };

  // Trigger auth enforcement based on the app settings
  enforceAuthIfRequired({
    user: animaUser,
    settings: animaSettings,
    auth: animaAuth,
  });
}
